import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import SeletorDeAtendente from "../SeletorDeAtendente";

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketWhatsappId }) => {
	const navigate = useNavigate();
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState("");
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId);
	const { findAll: findAllQueues } = useQueues();
	const { loadingWhatsapps, whatsApps } = useWhatsApps();

	const { user: loggedInUser } = useContext(AuthContext);

	// Só busca as filas com a janela ABERTA.
	//
	// Este modal é montado por LINHA da lista de atendimentos (fechado, à
	// espera do clique em "Transferir"), e o efeito rodava no mount de cada
	// um: abrir a tela com 40 conversas disparava 40 vezes `/queue`, e o
	// medido em produção (10/09/2026) foram 143 chamadas numa abertura, com
	// média de 10,5 s e pico de 20 s — a tela inteira ficava esperando por
	// uma lista que nem estava na tela.
	useEffect(() => {
		if (!modalOpen) return;

		const loadQueues = async () => {
			const list = await findAllQueues();
			setAllQueues(list);
			setQueues(list);
		};
		loadQueues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalOpen]);

	const handleClose = () => {
		onClose();
		setSelectedUser(null);
	};

	const handleSelectUser = (user) => {
		setSelectedUser(user);
		if (user != null && Array.isArray(user.queues)) {
			setQueues(user.queues);
		} else {
			setQueues(allQueues);
			setSelectedQueue("");
		}
	};

	const handleSaveTicket = async (e) => {
		e.preventDefault();
		if (!ticketid) return;
		setLoading(true);
		try {
			let data = {};

			if (selectedUser) {
				data.userId = selectedUser.id;
			}

			if (selectedQueue && selectedQueue !== null) {
				data.queueId = selectedQueue;

				if (!selectedUser) {
					data.status = "pending";
					data.userId = null;
				}
			}

			if (selectedWhatsapp) {
				data.whatsappId = selectedWhatsapp;
			}

			await api.put(`/tickets/${ticketid}`, data);

			setLoading(false);
			navigate(`/tickets`);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<Dialog open={modalOpen} onOpenChange={(o) => !o && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{i18n.t("transferTicketModal.title")}</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSaveTicket} className="space-y-4">
					<div className="space-y-1.5">
						<Label>{i18n.t("transferTicketModal.fieldLabel")}</Label>
						<SeletorDeAtendente
							aberto={modalOpen}
							valor={selectedUser}
							aoEscolher={handleSelectUser}
							placeholder={i18n.t("transferTicketModal.fieldLabel")}
						/>
					</div>

					<div className="space-y-1.5">
						<Label>{i18n.t("transferTicketModal.fieldQueueLabel")}</Label>
						<Select
							value={selectedQueue ? String(selectedQueue) : "none"}
							onValueChange={(v) => setSelectedQueue(v === "none" ? "" : v)}
						>
							<SelectTrigger>
								<SelectValue placeholder={i18n.t("transferTicketModal.fieldQueuePlaceholder")} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="none">&nbsp;</SelectItem>
								{queues.map((queue) => (
									<SelectItem key={queue.id} value={String(queue.id)}>
										{queue.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-sm text-muted-foreground">
							Sem atendente escolhido, a conversa vai para Aguardando do
							departamento.
						</p>
					</div>

					<Can
						role={loggedInUser.profile}
						perform="ticket-options:transferWhatsapp"
						yes={() =>
							!loadingWhatsapps && (
								<div className="space-y-1.5">
									<Label>{i18n.t("transferTicketModal.fieldConnectionLabel")}</Label>
									<Select
										value={selectedWhatsapp ? String(selectedWhatsapp) : undefined}
										onValueChange={(v) => setSelectedWhatsapp(v)}
									>
										<SelectTrigger>
											<SelectValue placeholder={i18n.t("transferTicketModal.fieldConnectionPlaceholder")} />
										</SelectTrigger>
										<SelectContent>
											{whatsApps.map((whasapp) => (
												<SelectItem key={whasapp.id} value={String(whasapp.id)}>
													{whasapp.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							)
						}
					/>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
							{i18n.t("transferTicketModal.buttons.cancel")}
						</Button>
						<Button type="submit" disabled={loading}>
							{loading && <Loader2 className="h-4 w-4 animate-spin" />}
							{i18n.t("transferTicketModal.buttons.ok")}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default TransferTicketModal;
